import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';

export default function PaymentAccountVerificationPanel({
  secureFetch,
  paymentAlias,
  paymentCbu,
  paymentHolderName,
  modoOscuro,
  textPri,
  textMuted,
}) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasInput = Boolean(
    String(paymentAlias || '').trim()
    || String(paymentCbu || '').trim()
  );

  const runVerify = useCallback(async () => {
    if (!hasInput) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await secureFetch('/api/notifications/verify-payment-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentAlias,
          paymentCbu,
          paymentHolderName,
        }),
      });
      const data = await res.json();
      if (res.ok) setReport(data);
      else setReport(null);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, paymentAlias, paymentCbu, paymentHolderName, hasInput]);

  useEffect(() => {
    const t = window.setTimeout(runVerify, 450);
    return () => window.clearTimeout(t);
  }, [runVerify]);

  if (!hasInput) {
    return (
      <p className={`text-xs ${textMuted}`}>
        Cargá alias o CBU/CVU para verificar formato y comparar el titular con tu perfil.
      </p>
    );
  }

  const tone = report?.readyToConfirm
    ? (report.holder?.matchesProfile ? 'ok' : 'warn')
    : 'error';

  const boxClass = tone === 'ok'
    ? (modoOscuro ? 'border-green-800/50 bg-green-950/20' : 'border-green-200 bg-green-50/80')
    : tone === 'warn'
      ? (modoOscuro ? 'border-amber-800/50 bg-amber-950/20' : 'border-amber-200 bg-amber-50/80')
      : (modoOscuro ? 'border-red-900/50 bg-red-950/20' : 'border-red-100 bg-red-50/80');

  const Icon = tone === 'ok' ? FiCheckCircle : tone === 'warn' ? FiAlertTriangle : FiInfo;
  const iconClass = tone === 'ok'
    ? (modoOscuro ? 'text-green-300' : 'text-green-700')
    : tone === 'warn'
      ? (modoOscuro ? 'text-amber-300' : 'text-amber-700')
      : (modoOscuro ? 'text-red-300' : 'text-red-700');

  return (
    <div className={`rounded-xl border px-3 py-3 text-xs space-y-2 ${boxClass}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconClass}`} />
        <div className="min-w-0">
          <p className={`font-semibold ${textPri}`}>
            {loading ? 'Verificando…' : (report?.summary || 'Verificando datos…')}
          </p>
          {report && !loading && (
            <ul className={`mt-2 space-y-1 ${textMuted}`}>
              {report.alias?.provided && (
                <li>{report.alias.message}</li>
              )}
              {report.cbu?.provided && (
                <li>{report.cbu.message}</li>
              )}
              {report.holder?.provided && (
                <li>{report.holder.message}</li>
              )}
            </ul>
          )}
        </div>
      </div>
      {report?.readyToConfirm && (
        <p className={`${textMuted} pl-6`}>
          En tu app del banco o Mercado Pago, buscá el alias/CBU y confirmá que el titular coincida con lo que cargaste.
        </p>
      )}
    </div>
  );
}
